import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Progress } from "@/components/ui/progress"
import { CheckCircle, Circle, Calendar, Clock, Loader, FilePenLine, Star } from "lucide-react"
import type { Objective } from "@/services/api/cronos/weekly-objectives/get"
import useGetActivities from "../../queries/use-get-activities"
import { Chip } from "@/components/ui/chip"
import type { WeeklyOfWeekly } from "../../entities/weekly-objectives/weekly-objectives"
import { intervalToDuration } from "date-fns"
import { formatDuration } from "@/shared/utils/date-fn"
import { Skeleton } from "@/components/ui/skeleton"
import { useEffect, useMemo } from "react"
import type { Unit } from "@/services/api/cronos/activities/get"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"
import type { Course } from "@/modules/courses/entities/courses"
import { useWeeklyObjectivesStore } from "../../stores/use-weekly-objectives-store"

interface WeeklyObjectivesDisplayProps {
  open: boolean
  courses: Course[]
  column: WeeklyOfWeekly
  objective: Objective
  onOpenChange: (open: boolean) => void
}

export default function WeeklyObjectivesDisplay({
  open,
  courses,
  column,
  objective,
  onOpenChange,
}: WeeklyObjectivesDisplayProps) {
  const activitiesQuery = useGetActivities({
    userWeeklyObjectiveId: objective.id,
  })
  const { selectedWeeklyObjective } = useWeeklyObjectivesStore()
  const { getCourseColor } = useCoursesInfo()

  const pendingActivities =
    activitiesQuery.data?.filter(
      (activity) => activity.status !== "approved" && activity.status !== "skipped",
    ) || []

  const skippedActivities =
    activitiesQuery.data?.filter((activity) => activity.status === "skipped") || []

  const approvedActivities =
    activitiesQuery.data?.filter((activity) => activity.status === "approved") || []

  const activitiesBasedOnResults =
    activitiesQuery.data
      ?.filter((activity) => activity.type === "diagnostics" || activity.type === "flashcards")
      .filter((act) => act.status !== "skipped") || []

  const approvedActivitiesCount =
    activitiesBasedOnResults?.filter((act) => act.status === "approved").length || 0

  const totalActivities = activitiesBasedOnResults?.length || 0

  const progressValue = useMemo(() => {
    return totalActivities > 0 ? Math.floor((approvedActivitiesCount / totalActivities) * 100) : 0
  }, [approvedActivitiesCount, totalActivities])

  const duration = intervalToDuration({
    start: 0,
    end: selectedWeeklyObjective.estimatedDuration,
  })

  const formattedDuration = formatDuration(duration).min()

  const course = courses.find((c) => c.subjectId === objective.subjectId)
  const courseColor = getCourseColor(objective.subjectId)

  useEffect(() => {
    if (open) {
      activitiesQuery.refetch()
    }
  }, [open])

  const filteredUnits: Unit[] = useMemo(() => {
    return Array.from(
      new Map(
        activitiesQuery.data
          ?.filter((activity) => activity.unit?.id)
          .map((activity) => [activity.unit.id, activity.unit]),
      ).values(),
    )
  }, [activitiesQuery.data])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-96 overflow-y-auto">
        <SheetHeader className="mt-6 space-y-4">
          <div className="flex w-full flex-col gap-2">
            <SheetTitle className="font-regular text-sm text-neutral-500">
              Objetivo semanal N° {objective.weeklyObjectiveIndex}
            </SheetTitle>
            <Chip size="sm" color={courseColor} className="w-fit">
              {course?.name}
            </Chip>
          </div>
          <SheetTitle className="text-lg font-bold leading-[120%]">{objective.name}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-8">
          <div className="flex items-center space-x-2 text-sm">
            <Calendar className="h-4 w-4 text-neutral-500" />
            <span className="text-neutral-700">Semana:</span>
            <span className="font-medium text-neutral-500">{column.week}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Clock className="h-4 w-4 text-neutral-500" />
            <span className="text-neutral-700">Carga:</span>
            <span className="font-medium text-neutral-500">{formattedDuration}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-x-2">
              <Loader className="h-4 w-4 text-neutral-500" />
              <span className="text-neutral-700">Progreso:</span>
            </div>
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full px-4">
                <Progress value={progressValue} className="h-2 w-full bg-neutral-200" />
              </div>
              <span className="font-medium text-neutral-500">{progressValue}%</span>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Temas</h4>
          <div>
            <ul className="mt-2 space-y-4">
              {activitiesQuery.isFetching
                ? Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-4 w-3/4" />
                  ))
                : filteredUnits.map((unit) => (
                    <li key={unit.id} className="flex items-start">
                      <FilePenLine className="mr-[15px] mt-[2px] h-4 w-4 shrink-0 text-primary-500" />
                      <span className="text-sm tracking-[0%] text-secondary-foreground">
                        {unit.name}
                      </span>
                    </li>
                  ))}
            </ul>
          </div>
        </div>
        <div className="space-y-4 pt-8">
          <h4 className="text-sm font-semibold">Detalle de actividades</h4>
          <div className="space-y-3">
            {activitiesQuery.isFetching ? (
              Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="mt-2 space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))
            ) : (
              <>
                {objective.status !== "approved" && (
                  <>
                    <h5 className="text-sm font-medium text-muted-foreground">Pendientes</h5>
                    {pendingActivities.map((activity, index) => (
                      <div key={index} className="mt-4">
                        <div className="flex items-start gap-2">
                          <Circle className="mr-2 mt-[4px] h-4 w-4 shrink-0 text-primary-500" />
                          <span className="flex-1 text-sm text-foreground">{activity.name}</span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {objective.status === "approved" && skippedActivities.length > 0 && (
                  <>
                    <h5 className="text-sm font-medium text-muted-foreground">Ahorradas</h5>
                    {skippedActivities.map((activity, index) => (
                      <div key={index} className="mt-2">
                        <div className="flex items-start">
                          <Star className="mr-3 mt-[3px] h-4 w-4 shrink-0 text-primary-500" />
                          <span className="flex-1 text-sm text-foreground">{activity.name}</span>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                <h5 className="text-sm font-medium text-muted-foreground">Realizadas</h5>
                {approvedActivities.map((activity, index) => (
                  <div key={index} className="mt-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="mr-1 mt-[4px] !size-4 shrink-0 text-primary-500" />
                      <span className="flex-1 text-sm text-neutral-700 line-through">
                        {activity.name}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
